import axios from "axios";
import { authHeader } from "@tailormed/common-client/services/auth";
import { getAuthInstance } from "@tailormed/common-client/services/auth";
import { oktaConfig } from "../constant/config";
import _ from "lodash";
import { PAGE_SIZE } from "../constant/general";

const SERVER_URL = `${window.location.origin.replace("4400", "1776")}/`;
const API_VERSION_1 = "api/v1/";
const PHARMACY_PATIENTS = "pharmacypatients/";
const PATIENT_ORDERS = "patientOrders/";
const USERS = "users/";
const ApiBaseUrl = `${SERVER_URL}${API_VERSION_1}`;
const SOURCE = "pharmacy";
const PATIENT_ATTACHMENTS = "patientattachments/";

console.log("--------------*E&A&R*---------------");
console.log("VERSION:", process.env.REACT_APP_VERSION);
console.log("SERVER_URL:", SERVER_URL);
console.log("--------------*E&A&R*---------------");

axios.defaults.withCredentials = true;

const ApiMap = {};
const ApiCallerMap = {};

const CallerType = Object.freeze({
  Generic: 0,
  Auth: 1
});

const ApiNames = Object.freeze({
  Login: "Login",
  LoggedUser: "LoggedUser",
  Settings: "Settings",
  Assignees: "Assignees",
  Users: "Users",
  Manufacturers: "Manufacturers",
  GetUnits: "GetUnits",
  PatientPhiInfo: "PatientPhiInfo",
  GetCycles: "GetCycles",
  Search: "Search",
  SearchWithoutRefresh: "SearchWithoutRefresh",
  SearchFreeText: "SearchFreeText",
  EditPatientCycle: "EditPatientCycle",
  NextCycle: "NextCycle",
  CycleDelete: "CycleDelete",
  BulkChange: "BulkChange",
  PatientOrders: "PatientOrders",
  PharmacyPatients: "PharmacyPatients",
  PharmacyPatientsAdd: "PharmacyPatientsAdd",
  PatientAutomateAfterCreate: "PatientAutomateAfterCreate",
  Notes: "Notes",
  NotesAdd: "NotesAdd",
  NotesEdit: "NotesEdit",
  NoteDelete: "NoteDelete",
  Attachments: "Attachments",
  AttachmentsUpload: "AttachmentsUpload",
  AttachmentsDelete: "AttachmentsDelete",
  Tasks: "Tasks",
  TasksAdd: "TasksAdd",
  TasksDelete: "TasksDelete",
  TasksEdit: "TasksEdit",
  Tracking: "Tracking",
  Support: "Support",
  Clone: "Clone",
  EmailConfiguration: "EmailConfiguration",
  NotificationPreferences: "NotificationPreferences",
  UpdateNotificationPreferences: "UpdateNotificationPreferences",
  UserGroups: "UserGroups",
  UserGroupsWithUsers: "UserGroupsWithUsers",
  SearchOrder: "SearchOrder",
  SearchByTextAndFilters: "SearchByTextAndFilters",
  CreateFilterList: "CreateFilterList",
  searchSavedList: "searchSavedList",
  UpdateFilterList: "UpdateFilterList",
  FindSavedListById: "FindSavedListById",
  GetRecentPatients: "GetRecentPatients"
});

async function axiosWithAuthentication(config) {
  const oktaAuth = getAuthInstance(oktaConfig.redirectUri);
  const token = oktaAuth.getAccessToken();
  config.headers = authHeader(token);
  return axios(config);
}

export const orderMap = {
  ascend: "ASC",
  descend: "DESC"
};

const exportListAPI = ({
  listType,
  selectedView,
  filters = {},
  sorting = {},
  visibleColumns = [],
  savedListId = null,
  fileType = "csv"
}) => {
  const {
    showInactiveFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned
  } = filters;

  const formattedSorting = sorting?.columnKey
    ? {
        orderBy: sorting.columnKey,
        sort: orderMap[sorting.order]
      }
    : {};

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}report-data`,
    data: {
      listType,
      selectedView,
      filters: {
        showHistoricOrders: showInactiveFilter,
        orderDateFilter,
        adminDateFilter,
        estimatedDeliveryDateFilter,
        receivedDateFilter,
        billVoidedDateFilter,
        assigneesFilter,
        facilitiesFilter,
        manufacturersFilter,
        physiciansFilter,
        statusesFilter,
        orderTypesFilter,
        billVoidedFilter,
        drugsFilter,
        drugDoseFormCategoriesFilter,
        orderViewFilter,
        isUnassigned
      },
      sorting: formattedSorting,
      visibleColumns,
      savedListId,
      fileType,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  });
};

const formateSorting = (sorter) => ({
  ...(sorter && {
    sorting: {
      orderBy: sorter.columnKey,
      sort: orderMap[sorter?.order]
    }
  })
});

const fetch =
  (action) =>
  (methodName) =>
  (...params) =>
    new Promise((resolve, reject) => {
      const req = action(...params);
      axios(req)
        .then((res) => resolve(res.data))
        .catch((error) => reject(Object.assign(error, { methodName })));
    });

const add = (name, action, caller = CallerType.Generic) => {
  Object.assign(ApiCallerMap, { [name]: caller });
  Object.assign(ApiMap, { [name]: (...args) => action(...args) });
};

const addAuth = (name, action) => add(name, action, CallerType.Auth);

const viewPatientAttachmentURL = (attachmentId, attachmentUniqueId) =>
  `${ApiBaseUrl}${PATIENT_ATTACHMENTS}view/${attachmentId}/${attachmentUniqueId}`;

add(ApiNames.Login, (username, password) => {
  const data = new FormData();
  data.append("username", username);
  data.append("password", password);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}token/pharmacy`,
    data
  };
});

addAuth(ApiNames.LoggedUser, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}logged-user/pharmacy`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Users, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}frontend`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Settings, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}settings`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Assignees, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}assignable`,
  headers: authHeader(token)
}));

addAuth(
  ApiNames.Search,
  (
    token,
    showHistoricOrders,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned,
    skip,
    limit,
    sorting,
    noPagination
  ) => {
    const data = {
      query: {
        showHistoricOrders,
        orderDateFilter,
        adminDateFilter,
        estimatedDeliveryDateFilter,
        receivedDateFilter,
        billVoidedDateFilter,
        assigneesFilter,
        facilitiesFilter,
        manufacturersFilter,
        physiciansFilter,
        statusesFilter,
        orderTypesFilter,
        billVoidedFilter,
        drugsFilter,
        drugDoseFormCategoriesFilter,
        orderViewFilter,
        isUnassigned
      },
      ...formateSorting(sorting),
      noPagination
    };
    return {
      method: "POST",
      url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}dashboard/search?limit=${limit}&skip=${skip}`,
      headers: authHeader(token),
      data
    };
  }
);

addAuth(
  ApiNames.SearchWithoutRefresh,
  (
    token,
    showHistoricOrders,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned,
    skip,
    limit,
    sorting,
    noPagination
  ) => {
    const data = {
      query: {
        showHistoricOrders,
        orderDateFilter,
        adminDateFilter,
        estimatedDeliveryDateFilter,
        receivedDateFilter,
        billVoidedDateFilter,
        assigneesFilter,
        facilitiesFilter,
        manufacturersFilter,
        physiciansFilter,
        statusesFilter,
        orderTypesFilter,
        billVoidedFilter,
        drugsFilter,
        drugDoseFormCategoriesFilter,
        orderViewFilter,
        isUnassigned
      },
      ...formateSorting(sorting),
      noPagination
    };
    return {
      method: "POST",
      url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}dashboard/search?limit=${limit}&skip=${skip}`,
      headers: authHeader(token),
      data
    };
  }
);

const getOrderByIdAPI = (orderId) => {
  const data = {};
  data.query = {
    orderId,
    orderViewFilter: "all_orders"
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}dashboard/search`,
    data
  });
};

const getPharmacyPatientByIdAPI = (pharmacyPatient) => {
  const data = new FormData();
  data.append("pharmacyPatient", pharmacyPatient);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}popup-info-phi`,
    data
  });
};

addAuth(ApiNames.getAPSponsors, (token) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprograms/getAPSponsors`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.UserGroups, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}userGroups`,
  headers: authHeader(token)
}));

addAuth(ApiNames.UserGroupsWithUsers, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}userGroups/with-users`,
  headers: authHeader(token)
}));

addAuth(ApiNames.SearchFreeText, (token, page, text, searchBy, sorting) => {
  const data = {
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
    query: {
      string: text
    },
    searchBy,
    ...formateSorting(sorting)
  };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}dashboard/search`,
    headers: authHeader(token),
    data
  };
});

addAuth(
  ApiNames.SearchByTextAndFilters,
  (
    token,
    text,
    searchBy,
    showInactiveFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned,
    skip,
    limit,
    sorting,
    noPagination
  ) => {
    const data = {
      query: {
        string: text
      },
      searchBy,
      filters: {
        showHistoricOrders: showInactiveFilter,
        orderDateFilter,
        adminDateFilter,
        estimatedDeliveryDateFilter,
        receivedDateFilter,
        billVoidedDateFilter,
        assigneesFilter,
        facilitiesFilter,
        manufacturersFilter,
        physiciansFilter,
        statusesFilter,
        orderTypesFilter,
        billVoidedFilter,
        drugsFilter,
        drugDoseFormCategoriesFilter,
        orderViewFilter,
        isUnassigned
      },
      ...formateSorting(sorting),
      noPagination
    };
    return {
      method: "POST",
      url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}dashboard/search?limit=${limit}&skip=${skip}`,
      headers: authHeader(token),
      data
    };
  }
);

addAuth(ApiNames.Manufacturers, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}manufacturers`,
  headers: authHeader(token)
}));

addAuth(ApiNames.GetUnits, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}setting/getAllUnits`,
  headers: authHeader(token)
}));

addAuth(ApiNames.PatientPhiInfo, (token, pharmacyPatient) => {
  const data = new FormData();
  data.append("pharmacyPatient", pharmacyPatient);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}popup-info-phi`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.GetCycles, (token, pharmacyPatient, adminDateFilter = null) => {
  const data = {
    pharmacyPatient,
    sort: "ASC",
    ...(adminDateFilter && { adminDateFilter })
  };

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}get-all-cycles`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.NextCycle, (token, pharmacyPatient, currentOrderIndex) => {
  const data = new FormData();
  data.append("pharmacyPatient", pharmacyPatient);
  data.append("currentOrderIndex", currentOrderIndex);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}next-cycle`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.CycleDelete, (token, id) => {
  return {
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}patientOrders/${id}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PharmacyPatients, (token, pharmacyPatient) => {
  return {
    method: "put",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}${pharmacyPatient.id}`,
    headers: authHeader(token),
    data: { "pharmacy-patient": pharmacyPatient }
  };
});

addAuth(ApiNames.PharmacyPatientsAdd, (token, patientData) => {
  const { medicationOrderData, rxnormDrug, mrn, dateOfBirth, firstName, middleName, lastName, selectedProgram } =
    patientData;

  const data = {
    assistanceProgram: selectedProgram,
    medicationOrderData,
    rxnormDrug,
    mrn,
    dob: dateOfBirth,
    firstName: firstName,
    middleName: middleName,
    lastName: lastName,
    source: SOURCE
  };

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/newpatient`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.PatientAutomateAfterCreate, (token, patientId) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/automateNewPatient/${patientId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.EditPatientCycle, (token, pharmacyPatient, patientCycle) => {
  return {
    method: "put",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ORDERS}${pharmacyPatient}`,
    headers: authHeader(token),
    data: { "patient-order": patientCycle }
  };
});

addAuth(ApiNames.PatientOrders, (token, pharmacyPatient) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patientOrders`,
    headers: authHeader(token),
    data: { "patient-order": pharmacyPatient }
  };
});

addAuth(ApiNames.BulkChange, (token, pharmacyPatient, currentOrderId, orders) => {
  const data = currentOrderId ? { pharmacyPatient, currentOrderId, orders } : { pharmacyPatient, orders };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}update-bulk-change`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.Notes, (token, patientId) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("include_deleted", false);
  data.append("include_cycle_specific_notes", true);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.NotesAdd, (token, patientId, note, orderIndex, pharmacyPatient) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("note", note);
  orderIndex && data.append("orderIndex", orderIndex);
  data.append("pharmacyPatientId", pharmacyPatient);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes/add`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.NotesEdit, (token, { noteId, note }) => {
  const data = new FormData();
  data.append("note_id", noteId);
  data.append("note", note);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes/edit`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.NoteDelete, (token, patientId, noteId, pharmacyPatient) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("note_id", noteId);
  data.append("pharmacyPatientId", pharmacyPatient);

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes/delete`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.Attachments, (token, patientId) => {
  const data = new FormData();
  data.append("patient", patientId);
  data.append("options", JSON.stringify({ excludeCylcesSpecific: false }));
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}list`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.AttachmentsUpload, (token, patientId, pharmacyPatient, file, orderIndex) => {
  const data = new FormData();
  data.append("patient", patientId);
  data.append("pharmacyPatient", pharmacyPatient);
  orderIndex && data.append("pharmacyPatientOrderIndex", orderIndex);
  data.append("file", file);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}upload`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.AttachmentsDelete, (token, id, attachmentUniqueId, pharmacyPatient) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}delete/${id}/${pharmacyPatient}/${attachmentUniqueId}`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Tasks, (token, patientId) => {
  const data = new FormData();
  data.append("patientId", patientId);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}todos/list`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.TasksAdd, (token, todo, pharmacyPatient) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}todos`,
    headers: authHeader(token),
    data: { todo, pharmacyPatientId: pharmacyPatient }
  };
});

addAuth(ApiNames.TasksEdit, (token, todo, pharmacyPatient) => {
  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}todos/${todo.id}`,
    headers: authHeader(token),
    data: { todo, pharmacyPatientId: pharmacyPatient }
  };
});

addAuth(ApiNames.TasksDelete, (token, todo, pharmacyPatient) => {
  const data = {};
  data.todo = { ...todo, status: "deleted" };
  data.pharmacyPatientId = pharmacyPatient;

  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}todos/${todo.id}`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.Tracking, (token, carrier, trackingNumber) => {
  const data = new FormData();
  data.append("carrier_name", carrier);
  data.append("tracking_number", trackingNumber);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}trackingnumbersearch`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.Support, (token, subject, comment, patient, user) => {
  const data = {};
  data.ticket = {
    subject,
    comment,
    patient,
    user,
    application: "Pharmacy"
  };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}support/create-ticket`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.Clone, (token, pharmacyPatientId) => {
  const data = new FormData();
  data.append("pharmacyPatientId", pharmacyPatientId);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}clone-pharmacy-patient`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.EmailConfiguration, (token, { triggerName, active }) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}email-user-configuration`,
    headers: authHeader(token),
    data: { triggerName, active, appType: "pharmacy" }
  };
});

addAuth(ApiNames.NotificationPreferences, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}notification-preferences`,
  headers: authHeader(token)
}));

addAuth(ApiNames.UpdateNotificationPreferences, (token, preferenceType, targetId, notificationType, isEnabled) => ({
  method: "PUT",
  url: `${SERVER_URL}${API_VERSION_1}notification-preferences`,
  headers: authHeader(token),
  data: {
    preferenceType,
    targetId,
    notificationType,
    isEnabled
  }
}));

addAuth(ApiNames.GetRecentPatients, (token, page = 1) => {
  const limit = PAGE_SIZE;

  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/getRecentPharmacyPatients?product=pharmacy&page=${page}&limit=${limit}`,
    headers: authHeader(token)
  };
});

const getDrugsNameAPI = (papId, drugName) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}drugs?papId=${papId}&name=${drugName}`
  });

const getPapDrugCostCalculation = (papId) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}drugs/pap-drug-cost-calculation?papId=${papId}`
  });

const putDrugCostCalculationAPI = (papId, data) =>
  axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}paps/${papId}/drugCostCalculation`,
    data
  });

const SearchAssistanceProgramAPI = (text) => {
  const data = new FormData();
  data.append("query", text);
  data.append("isFreeDrug", "1");
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprograms/search`,
    data
  });
};

const downloadPatientAttachmentsAPI = (id, attachmentUniqueId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}download/${id}/${attachmentUniqueId}`,
    responseType: "blob"
  });
};

const getLoginImagesAPI = () =>
  axios({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}app/login-images/pharmacy`
  });

const searchPhysiciansAPI = (searchQuery, { limit = 50, includeInactive = false } = {}) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}providers/searchProviders?type=doctor&limit=${limit}&includeInactive=${includeInactive}`,
    data: { searchQuery }
  });
};

const searchFacilitiesAPI = (searchQuery, { limit = 50 } = {}) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}providers/searchProviders?type=facility&limit=${limit}`,
    data: { searchQuery }
  });
};

const searchAPSponsorsAPI = (searchQuery) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprograms/getAPSponsors?query=${encodeURIComponent(searchQuery)}`
  });
};

const searchProvidersByTypeAPI = (type, { limit = 100, searchQuery = "" } = {}) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}providers/searchProviders?type=${type}&limit=${limit}`,
    data: { searchQuery }
  });
};

const fetchProvidersByIdsAPI = (ids) => {
  if (!ids || ids.length === 0) return Promise.resolve({ data: [] });
  const where = JSON.stringify({ id: { in: ids } });
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}providers?where=${encodeURIComponent(where)}`
  });
};

const searchRxnormDrugsAPI = ({ text = "", bnCui = "", pinCui = "" }) => {
  const queryObject = _.omitBy(
    {
      query: text,
      bnCui,
      pinCui
    },
    (param) => !param
  );

  const params = new URLSearchParams(queryObject);

  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}rxnorm-drug/search?${params}`
  });
};

addAuth(ApiNames.CreateFilterList, (token, formData) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/new-filter-list`,
    headers: authHeader(token),
    data: formData.data
  };
});

const getNumberOfPatientsAPI = (filters) => {
  const {
    showInactiveFilter,
    orderDateFilter,
    adminDateFilter,
    estimatedDeliveryDateFilter,
    receivedDateFilter,
    billVoidedDateFilter,
    assigneesFilter,
    facilitiesFilter,
    manufacturersFilter,
    physiciansFilter,
    statusesFilter,
    orderTypesFilter,
    billVoidedFilter,
    drugsFilter,
    drugDoseFormCategoriesFilter,
    orderViewFilter,
    isUnassigned
  } = filters;
  const data = {
    query: {
      showHistoricOrders: showInactiveFilter,
      orderDateFilter,
      adminDateFilter,
      estimatedDeliveryDateFilter,
      receivedDateFilter,
      billVoidedDateFilter,
      assigneesFilter,
      facilitiesFilter,
      manufacturersFilter,
      physiciansFilter,
      statusesFilter,
      orderTypesFilter,
      billVoidedFilter,
      drugsFilter,
      drugDoseFormCategoriesFilter,
      orderViewFilter,
      isUnassigned
    }
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}dashboard/search`,
    data
  });
};

addAuth(ApiNames.searchSavedList, (token, data) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/search-filter-list`,
    headers: authHeader(token),
    data
  };
});

const starFilterList = (filterId, starred) => {
  const data = { filterId, starred };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/star-filter-list`,
    data: data
  });
};

const deleteSavedListAPI = (listId) => {
  return axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/delete/` + listId
  });
};

addAuth(ApiNames.UpdateFilterList, (token, data, filterListId) => {
  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/update/${filterListId}`,
    headers: authHeader(token),
    data: data
  };
});

addAuth(ApiNames.FindSavedListById, (token, filterListId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/${filterListId}`,
    headers: authHeader(token)
  };
});

const api = (name) => fetch(ApiMap[name])(name);

export {
  api,
  ApiNames,
  ApiMap,
  ApiCallerMap,
  CallerType,
  SERVER_URL,
  ApiBaseUrl,
  SearchAssistanceProgramAPI,
  getLoginImagesAPI,
  downloadPatientAttachmentsAPI,
  getDrugsNameAPI,
  getPapDrugCostCalculation,
  putDrugCostCalculationAPI,
  searchRxnormDrugsAPI,
  searchPhysiciansAPI,
  searchFacilitiesAPI,
  searchAPSponsorsAPI,
  searchProvidersByTypeAPI,
  fetchProvidersByIdsAPI,
  getOrderByIdAPI,
  getPharmacyPatientByIdAPI,
  getNumberOfPatientsAPI,
  starFilterList,
  deleteSavedListAPI,
  viewPatientAttachmentURL,
  exportListAPI
};
